// 📦 Simple stale-while-revalidate cache for GET API responses.
// Why: on repeat opens, show the LAST-seen data instantly while a fresh fetch
// runs in the background. Works offline too — if the network dies we keep
// serving the last cached payload with a visual banner.
import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { api } from "./api";

const NATIVE = Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("Filesystem");
const FS_DIR = "data-cache";
const fileOf = (key) => `${FS_DIR}/${encodeURIComponent(key)}.json`;

const PREFIX = "kh_cache_v1:";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;   // persist up to 30 days
const _memory = new Map();

function _read(key) {
  const mem = _memory.get(key);
  if (mem) return mem;
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry?.ts || Date.now() - entry.ts > MAX_AGE_MS) {
      localStorage.removeItem(PREFIX + key);
      return null;
    }
    _memory.set(key, entry);
    return entry;
  } catch { return null; }
}

// Native: load every stored payload from phone storage into memory before
// the first render so all screens paint instantly with zero network.
export async function hydrateCache() {
  if (!NATIVE) return;
  try {
    const { files } = await Filesystem.readdir({ path: FS_DIR, directory: Directory.Data });
    await Promise.all(files.map(async (f) => {
      const name = typeof f === "string" ? f : f.name;
      try {
        const r = await Filesystem.readFile({ path: `${FS_DIR}/${name}`, directory: Directory.Data, encoding: Encoding.UTF8 });
        const key = decodeURIComponent(name.replace(/\.json$/, ""));
        _memory.set(key, JSON.parse(r.data));
      } catch { /* corrupt file — ignore */ }
    }));
  } catch {
    await Filesystem.mkdir({ path: FS_DIR, directory: Directory.Data, recursive: true }).catch(() => {});
  }
}

function _write(key, data) {
  const entry = { ts: Date.now(), data };
  _memory.set(key, entry);
  if (NATIVE) {
    Filesystem.writeFile({ path: fileOf(key), data: JSON.stringify(entry), directory: Directory.Data,
      encoding: Encoding.UTF8, recursive: true }).catch(() => {});
    return;
  }
  try { localStorage.setItem(PREFIX + key, JSON.stringify(entry)); }
  catch { /* quota exceeded — silently drop oldest key */
    try {
      const keys = Object.keys(localStorage).filter(k => k.startsWith(PREFIX));
      keys.slice(0, Math.max(1, Math.floor(keys.length / 4))).forEach(k => localStorage.removeItem(k));
      localStorage.setItem(PREFIX + key, JSON.stringify(entry));
    } catch { /* give up */ }
  }
}

export function invalidateCache(prefix = "") {
  for (const k of _memory.keys()) {
    if (!k.startsWith(prefix)) continue;
    _memory.delete(k);
    if (NATIVE) Filesystem.deleteFile({ path: fileOf(k), directory: Directory.Data }).catch(() => {});
  }
  for (const k of Object.keys(localStorage)) {
    if (k.startsWith(PREFIX + prefix)) localStorage.removeItem(k);
  }
}

// Hook: returns [data, { loading, error, isFromCache, refresh }].
// First render returns cached data instantly if available, then fetches fresh.
export function useCachedGet(path, { enabled = true } = {}) {
  const key = path || "";
  const first = useRef(true);
  const [state, setState] = useState(() => {
    const cached = key ? _read(key) : null;
    return cached
      ? { data: cached.data, loading: true, error: null, isFromCache: true }
      : { data: null, loading: !!enabled, error: null, isFromCache: false };
  });

  const fetchFresh = async () => {
    if (!enabled || !key) return;
    try {
      const { data } = await api.get(path);
      _write(key, data);
      setState({ data, loading: false, error: null, isFromCache: false });
    } catch (e) {
      // Keep showing cached data if we have it; only surface the error when
      // we have nothing to show.
      setState(prev => ({ ...prev, loading: false, error: e, isFromCache: !!prev.data }));
    }
  };

  useEffect(() => {
    if (!enabled) return;
    if (first.current) {
      first.current = false;
      // Re-fire if nothing cached, OR fire in the background to refresh
      fetchFresh();
    } else {
      fetchFresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled]);

  return [state.data, { ...state, refresh: fetchFresh }];
}

// Imperative read/write — useful when a page already has its own loader
// but still wants to persist/restore across sessions.
export const readCache = (key) => _read(key)?.data || null;
export const writeCache = (key, data) => _write(key, data);

// Re-run `fn` whenever the background sync stores a fresh copy of `key`.
export function useCacheUpdated(match, fn) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const on = (e) => { const k = e.detail?.key || ""; if (match(k)) ref.current(k); };
    window.addEventListener("kh:cache-updated", on);
    return () => window.removeEventListener("kh:cache-updated", on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// Paint from the stored copy immediately, then refresh from the network.
export function cachedGet(key, path, apply) {
  const c = readCache(key);
  if (c != null) apply(c);
  return api.get(path).then((r) => { _write(key, r.data); apply(r.data); }).catch(() => {});
}

export const withCatImages = (list) => {
  const map = readCache("categories-images") || {};
  return (list || []).map((c) => (map[c.slug]
    ? { ...c, image: map[c.slug].image || c.image, image_has_alpha: map[c.slug].image_has_alpha ?? c.image_has_alpha }
    : c));
};
